import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import Panel from "../components/Panel";
import KpiCard from "../components/KpiCard";
import { fmtDate } from "../lib/format";
import { CheckCircle2, Clock, AlertCircle } from "lucide-react";
import ExportMenu from "../components/ExportMenu";

const STATUS_TAG_COLOR = (status) => {
    if (status.includes("Approved") || status.includes("Live") || status.includes("Rollout")) return "#4af6c3";
    if (status.includes("Progress") || status.includes("Submitted") || status.includes("Investor") || status.includes("Filed") || status.includes("Negotiation")) return "#fb8b1e";
    if (status.includes("On Hold") || status.includes("Escalated")) return "#ff433d";
    return "#0068ff";
};

export default function Decisions() {
    const [data, setData] = useState(null);
    const [catFilter, setCatFilter] = useState("All");
    const [expanded, setExpanded] = useState(null);

    useEffect(() => { api.get("/decisions").then((r) => setData(r.data)); }, []);
    if (!data) return <div className="font-mono-fin text-sm text-[var(--text-secondary)]">// LOADING DECISION LOG...</div>;

    const cats = ["All", ...new Set(data.decisions.map((d) => d.category))];
    const filtered = data.decisions.filter((d) => catFilter === "All" || d.category === catFilter);

    return (
        <div className="space-y-4">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="font-mono-fin text-[10px] uppercase tracking-[0.25em] text-[var(--brand-amber)] mb-1">// DECISION LOG</div>
                    <h1 className="font-display font-black text-3xl">Every Strategic Move, Documented</h1>
                    <div className="font-mono-fin text-[11px] text-[var(--text-secondary)] uppercase tracking-wider mt-1">
                        Owner · Rationale · Impact · Traceability · Confidence — for Board & Auditor review
                    </div>
                </div>
                <ExportMenu kind="decisions" label="Board-Ready" testId="export-decisions" />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KpiCard testId="dl-total" label="Total Decisions" value={data.summary.total} format="custom" accent />
                <KpiCard testId="dl-approved" label="Approved / Executed" value={data.summary.by_status.find((s) => s.name.includes("Approved") || s.name.includes("Rollout"))?.count || 0} format="custom" />
                <KpiCard testId="dl-progress" label="In Progress" value={data.summary.by_status.filter((s) => s.name.includes("Progress") || s.name.includes("Submitted") || s.name.includes("Negotiation")).reduce((sum, s) => sum + s.count, 0)} format="custom" />
                <KpiCard testId="dl-conf" label="Avg Confidence" value={`${data.summary.avg_confidence}%`} format="custom" sub="Owner-attested" />
            </div>

            <div className="flex items-center gap-2 font-mono-fin text-[10px] uppercase tracking-wider flex-wrap">
                <span className="text-[var(--text-secondary)]">Category:</span>
                {cats.map((c) => (
                    <button
                        key={c}
                        data-testid={`dl-cat-${c.replace(/\s|\/|&/g, "").toLowerCase()}`}
                        onClick={() => setCatFilter(c)}
                        className={`px-3 py-1 border ${catFilter === c ? "border-[var(--brand-amber)] bg-[var(--brand-amber-dim)] text-[var(--text-primary)]" : "border-panel text-[var(--text-secondary)]"}`}
                    >
                        {c}
                    </button>
                ))}
            </div>

            <div className="space-y-2">
                {filtered.map((d) => {
                    const open = expanded === d.id;
                    return (
                        <div key={d.id} data-testid={`dl-${d.id}`} className={`border p-4 transition-colors ${open ? "border-[var(--brand-amber)] bg-[var(--brand-amber-dim)]" : "border-panel bg-panel hover:bg-[var(--bg-panel-hover)]"}`}>
                            <div className="flex items-start justify-between gap-3 cursor-pointer" onClick={() => setExpanded(open ? null : d.id)}>
                                <div className="flex items-start gap-3 flex-1 min-w-0">
                                    <div className="w-8 h-8 border border-[var(--brand-amber)] flex items-center justify-center shrink-0">
                                        <span className="text-[10px] font-mono-fin text-[var(--brand-amber)]">{d.id.replace("D-", "")}</span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-[10px] font-mono-fin uppercase tracking-wider px-1.5 py-0.5 border border-panel text-[var(--text-secondary)]">{d.category}</span>
                                            <span className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-muted)]">{fmtDate(d.date)}</span>
                                        </div>
                                        <div className="font-display font-bold text-sm mt-1 text-[var(--text-primary)]">{d.title}</div>
                                        <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mt-1">
                                            Owner: <span className="text-[var(--text-primary)]">{d.owner}</span> · Approver: <span className="text-[var(--text-primary)]">{d.approver}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="text-right shrink-0">
                                    <span className="text-[10px] font-mono-fin uppercase tracking-wider px-2 py-1 border" style={{ borderColor: STATUS_TAG_COLOR(d.status), color: STATUS_TAG_COLOR(d.status) }}>{d.status}</span>
                                    <div className="text-[10px] font-mono-fin text-[var(--text-secondary)] uppercase tracking-wider mt-1">Confidence <span style={{ color: d.confidence >= 75 ? "#4af6c3" : d.confidence >= 55 ? "#fb8b1e" : "#ff433d" }}>{d.confidence}%</span></div>
                                    <div className="text-[10px] font-mono-fin text-[var(--text-muted)] uppercase tracking-wider mt-1">Due {fmtDate(d.target_date)}</div>
                                </div>
                            </div>

                            {open && (
                                <div className="mt-4 pt-4 border-t border-panel grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <div className="text-[10px] font-mono-fin uppercase tracking-[0.15em] text-[var(--brand-amber)] mb-1">Rationale</div>
                                        <div className="text-xs text-[var(--text-primary)] font-body leading-relaxed">{d.rationale}</div>
                                    </div>
                                    <div>
                                        <div className="text-[10px] font-mono-fin uppercase tracking-[0.15em] text-pos mb-1">Expected Impact</div>
                                        <div className="text-xs text-[var(--text-primary)] font-body leading-relaxed">{d.expected_impact}</div>
                                    </div>
                                    <div className="md:col-span-2">
                                        <div className="text-[10px] font-mono-fin uppercase tracking-[0.15em] text-neu mb-1">Traceability · Data Sources</div>
                                        <div className="flex flex-wrap gap-2">
                                            {d.traceability.map((t, i) => (
                                                <span key={i} className="text-[10px] font-mono-fin uppercase tracking-wider border border-panel px-1.5 py-0.5 text-[var(--text-secondary)]">{t}</span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
